import React from 'react';
import { SectionHeading } from './Primitives';

/**
 * Gyan Mitra highlight section — reused verbatim across student and school VR Experiences pages.
 */
export default function GyanMitraSection() {
  return (
    <section className="px-6 py-16 bg-white" data-testid="gyan-mitra-section">
      <div className="mx-auto max-w-[1240px]">
        <SectionHeading
          eyebrow="AI IMMERSIVE + EXPERIENTIAL LEARNING"
          title="Gyan Mitra — a virtual AI companion inside VR"
          desc="Gyan Mitra is a virtual AI character that lives inside each learning module. Students ask questions naturally and receive verbal explanations in English and Hindi, so concepts stay grounded in the immersive experience."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.15fr_1fr]">
          {/* Gyan Mitra intro video (YouTube embed) */}
          <div className="relative overflow-hidden rounded-3xl border border-canvas-200 bg-black shadow-card">
            <div className="aspect-video">
              <iframe
                className="h-full w-full"
                src="https://www.youtube.com/embed/gkkQmHcfvlo?start=1&rel=0&modestbranding=1"
                title="Gyan Mitra — a virtual AI companion inside VR"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-canvas-200 bg-white p-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold tracking-widest text-purple">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-purple/60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-purple" /></span>
                LIVE INSIDE VR
              </span>
              <span className="rounded-full bg-canvas-100 px-3 py-1 text-[11px] font-bold text-navy">Voice-first</span>
              <span className="rounded-full bg-canvas-100 px-3 py-1 text-[11px] font-bold text-navy">English</span>
              <span className="rounded-full bg-canvas-100 px-3 py-1 text-[11px] font-bold text-navy">Hindi</span>
              <span className="rounded-full bg-white ring-1 ring-canvas-200 px-3 py-1 text-[11px] font-bold text-ink-500">Assamese — coming soon</span>
            </div>
          </div>

          {/* AI capability grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50 text-purple"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="7" width="16" height="12" rx="3"/><path d="M9 3v4M15 3v4M8 13h.01M12 13h.01M16 13h.01"/></svg></span>
              <div className="mt-3 text-[14.5px] font-bold text-navy">Ask naturally in VR</div>
              <p className="mt-1 text-[12.5px] text-ink-500">Voice questions are answered inside the module without breaking immersion.</p>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-royal-50 text-royal"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a4 4 0 0 0-4 4v1a3 3 0 0 0-2 5.6V15a4 4 0 0 0 4 4v1a2 2 0 1 0 4 0v-1a4 4 0 0 0 4-4v-1.4A3 3 0 0 0 16 8V7a4 4 0 0 0-4-4Z"/></svg></span>
              <div className="mt-3 text-[14.5px] font-bold text-navy">Explains concepts, not just facts</div>
              <p className="mt-1 text-[12.5px] text-ink-500">Answers stay grounded in the current chapter and learning outcomes.</p>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-50 text-cyan"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1l2.1-2.1M17 7l2.1-2.1"/><circle cx="12" cy="12" r="4"/></svg></span>
              <div className="mt-3 text-[14.5px] font-bold text-navy">Multilingual by design</div>
              <p className="mt-1 text-[12.5px] text-ink-500">Verbal responses today in English and Hindi. Assamese is on the way.</p>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-mint-50 text-mint"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12A8 8 0 1 1 6.34 6.34"/><path d="M22 4 12 14l-3-3"/></svg></span>
              <div className="mt-3 text-[14.5px] font-bold text-navy">Safe, curriculum-guided</div>
              <p className="mt-1 text-[12.5px] text-ink-500">Responses are bounded by the learning module and age-appropriate for Grade 8–9.</p>
            </div>
          </div>
        </div>

        {/* Sample interaction preview */}
        <div className="mt-8 rounded-3xl border border-canvas-200 bg-canvas-100 p-6 md:p-8">
          <div className="text-[11.5px] font-bold tracking-widest text-purple">SAMPLE INTERACTION · SOUND</div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-2xl bg-white border border-canvas-200 p-4">
              <div className="text-[11px] font-bold tracking-widest text-royal">STUDENT · VOICE</div>
              <p className="mt-2 text-[14px] text-navy">“Gyan Mitra, why does a larger vibration make a louder sound?”</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-purple to-cyan grain p-4 text-white">
              <div className="text-[11px] font-bold tracking-widest text-white/80">GYAN MITRA · VOICE</div>
              <p className="mt-2 text-[14px]">“A larger vibration has greater amplitude. It transfers more energy to the air, so we hear the sound as louder.”</p>
            </div>
          </div>
          <p className="mt-4 text-[12.5px] text-ink-500">Note: Gyan Mitra requires internet connectivity inside VR. Core learning modules continue to work offline.</p>
        </div>
      </div>
    </section>
  );
}
